import React, { useContext, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import EmployeeTerminate from '../../../../services/EmployeeTerminate';
import { toast } from 'react-toastify';
import { toLocaleFormatSlash } from '../../../../helpers/dateFormat';
import moment from 'moment';

interface ViewAndPrintTerminationModalProps {
  isOpen: boolean;
  toggle: () => void;
  terminationId: string;
  row?: any;
}

const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Row className="mb-2 print-row">
    <Col xs="12" md="4" className="text-muted font-weight-bold">
      {label}
    </Col>
    <Col xs="12" md="8" className="text-break">
      {value ?? '-'}
    </Col>
  </Row>
);

const ViewAndPrintTerminationModal: React.FC<ViewAndPrintTerminationModalProps> = ({
  isOpen,
  toggle,
  terminationId,
  row,
}) => {
  const { token }: any = useContext(AuthContext);
  const [downloading, setDownloading] = useState(false);

  const employeeName =
    row?.employee?.user?.firstName || row?.employee?.user?.lastName
      ? `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim()
      : '-';
  const status = row?.approvedAt ? 'Approved' : row?.deniedAt ? 'Denied' : 'Waiting';
  const isApproved = !!row?.approvedAt;
  const signedDocumentUrl = row?.signedDocumentUrl;
  const createdAt = row?.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : null;
  const lastWorkDay = row?.lastWorkDay ? toLocaleFormatSlash(row.lastWorkDay) : null;
  const willBeRehired = row?.willBeRehired === true ? 'Yes' : row?.willBeRehired === false ? 'No' : null;
  const policyViolationsText =
    row?.policyViolations?.length > 0
      ? row.policyViolations
          .map((pv: any) => pv?.name)
          .filter(Boolean)
          .join(', ')
      : null;
  const approvedAt = row?.approvedAt ? moment(row.approvedAt).format('MM/DD/YYYY') : null;
  const deniedAt = row?.deniedAt ? moment(row.deniedAt).format('MM/DD/YYYY') : null;
  const startDate = row?.employee?.startDate ? toLocaleFormatSlash(row.employee.startDate) : null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (isApproved && signedDocumentUrl) {
        try {
          const response = await fetch(signedDocumentUrl);
          if (!response.ok) throw new Error('Failed to fetch document');
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Termination_Signed_Document');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch {
          window.open(signedDocumentUrl, '_blank');
        }
      } else {
        const blob = await EmployeeTerminate(token).getTemplateDocument(terminationId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Termination_and_Separation_Form.docx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      toast.success('Download successfully');
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to download document.';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="termination-details-modal">
      <ModalHeader toggle={toggle}>Termination Details</ModalHeader>
      <ModalBody className="termination-details-body pt-0">
        <div className="d-flex justify-content-end">
          <Button color="primary" onClick={handleDownload} disabled={downloading} size="sm">
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
        </div>
        <style>{`
          @media print {
            .termination-details-modal .modal-footer,
            .termination-details-modal .modal-header .close { display: none !important; }
            .termination-details-modal .modal-dialog { max-width: 100%; }
            .termination-details-body { padding: 0.5rem 0; }
            .termination-details-body .text-uppercase { font-size: 0.9rem; margin-top: 0.75rem !important; }
            .print-row { break-inside: avoid; }
          }
        `}</style>
        <h6 className="text-uppercase text-muted mb-3 mt-2">Employee Information</h6>

        <DetailRow label="Employee" value={employeeName} />
        <DetailRow label="Start Date" value={startDate} />
        <DetailRow label="Job Title" value={row?.employee?.title?.name} />
        <DetailRow label="Department" value={row?.employee?.department?.name} />

        <h6 className="text-uppercase text-muted mb-3 mt-4">Request Information</h6>
        <DetailRow label="Status" value={status} />
        <DetailRow label="Date" value={createdAt} />
        <DetailRow label="Last Work Day" value={lastWorkDay} />
        <DetailRow label="Voluntary / Involuntary" value={row?.category} />
        <DetailRow label="Will Be Rehired" value={willBeRehired} />
        <DetailRow label="Reason" value={row?.reason} />
        <DetailRow label="Notes" value={row?.notes} />
        {approvedAt && <DetailRow label="Approved At" value={approvedAt} />}
        {deniedAt && <DetailRow label="Denied At" value={deniedAt} />}
        {row?.deniedReason && <DetailRow label="Denied Reason" value={row.deniedReason} />}

        <h6 className="text-uppercase text-muted mb-3 mt-4">Termination Request</h6>
        <DetailRow label="Classroom Observation" value={row?.classroomObservation} />
        <DetailRow label="30 Day Check In" value={row?.thirtyDayCheckIn} />
        <DetailRow label="One on One" value={row?.oneOnOne} />
        <DetailRow label="Feedback" value={row?.feedback} />
        <DetailRow label="Improvement: Feedback" value={row?.feedbackImprovement} />
        <DetailRow label="Performance Improvement Plan" value={row?.performanceImprovementPlan} />
        <DetailRow
          label="Improvement: Performance Improvement Plan"
          value={row?.performanceImprovementPlanImprovement}
        />
        <DetailRow label="Verbal Warning" value={row?.verbalWarning} />
        <DetailRow label="Improvement: Verbal Warning" value={row?.verbalWarningImprovement} />
        <DetailRow label="Disciplinary Action Notice" value={row?.disciplinaryActionNotice} />
        <DetailRow label="Improvement: Disciplinary Action Notice" value={row?.disciplinaryActionNoticeImprovement} />
        <DetailRow label="Back Up Plan" value={row?.backUpPlan} />
        <DetailRow label="Policy Violations" value={policyViolationsText} />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ViewAndPrintTerminationModal;
